// eslint-disable-next-line no-restricted-imports
import type { Moment } from 'moment';
import { deserializeYesplanAPIDateAndTimeOrNull } from './dateTimeUtilities';
import { printInHourDisplayFormat, printInInputDateFormat } from './dateTimePrinting';
import type {
  ActiveFilter,
  CustomDataTreeNode,
  FilterTreeField,
  FilterTreeItem,
  ListViewBooking,
  ListViewColumn,
  ListViewConfiguration,
  ListViewGroup,
  ListViewTableRow,
  TemporalStatus
} from '../listview/types';

/* Helpers to present the resource booking DTOs of the /resourcebookings route based
 * on a presentation configuration stored in Firestore (the 'listview' dashboard
 * type, schemaVersion 2, source 'resourcebookings').
 *
 * The configuration decides how a booking is displayed:
 *   - column.key: a DTO field ('resourceName', 'eventName', ...) or a custom data
 *     field addressed as 'customdata:<keyword>'; the 'start'/'end' columns read the
 *     effectiveStart/effectiveEnd of the booking (a set's earliest/latest child)
 *   - column.type: optional, one of 'datetime' | 'date' | 'time' | 'number'
 *   - column.label: the human-facing column name
 *   - column.visible: hide the column when explicitly false
 *
 * Filtering, pagination and the card/table row shapes are all plain functions on
 * the bookings list, so they can be unit tested without any component. */

/* The heterogeneous value a booking's cell can hold, across every DTO field and
   every custom data value */
export type ListViewCellValue = string | number | boolean | string[] | null;

/* An em dash: what both view modes show for a booking that has no value for a
   column, where the cell or row is shown at all */
export const NO_VALUE = '—';

/* The column the resource's name is in: the one a booking is read by, and the
   one the count of resources it books is shown beside */
export const RESOURCE_NAME_COLUMN_KEY = 'resourceName';

/* The column the count of booked resources is configured as. It is not shown
   as a column of its own: the count reads as part of the name it belongs to
   ("Projector × 2"), which is where both view modes put it - see
   bookingCount and renderedColumns. */
export const COUNT_COLUMN_KEY = 'number';

const CUSTOM_DATA_KEY_PREFIX = 'customdata:';

export function bookingCellValue(
  booking: ListViewBooking,
  column: ListViewColumn
): ListViewCellValue {
  if (column.key.startsWith(CUSTOM_DATA_KEY_PREFIX)) {
    const keyword = column.key.substring(CUSTOM_DATA_KEY_PREFIX.length);
    const value = (booking.customData || {})[keyword];
    return value === undefined ? null : value;
  }
  if (column.key === 'start') return booking.effectiveStart;
  if (column.key === 'end') return booking.effectiveEnd;
  /* column.key is not a fixed set of literals (it can also be a plain DTO
     field name chosen freely in the Firestore configuration), so a typed
     index signature isn't available on ListViewBooking itself */
  const value = (booking as unknown as Record<string, ListViewCellValue>)[column.key];
  return value === undefined ? null : value;
}

/* The typed boundary onto dateTimeUtilities' non-throwing deserializer:
   dateTimeUtilities.js is not type-checked, so the return type is annotated
   here rather than inferred */
export const parseListViewDateTime = (value: unknown): Moment | null =>
  deserializeYesplanAPIDateAndTimeOrNull(value);

/* The one definition of a booking being over, on an already-parsed clock: the
   marking of a row and the selection of the rows to show both ask it */
const hasEnded = (end: Moment | null, nowObject: Moment): boolean =>
  end !== null && end.isBefore(nowObject);

/* Where a booking stands relative to the clock: 'past' when it ended before
   now, 'active' while it is running (inclusive bounds), 'future' before it
   starts. Bookings without a parseable effectiveEnd can never be 'past' or
   'active'; without a parseable effectiveStart they are 'unknown' and get no
   temporal styling at all. */
export function temporalStatus(
  booking: Pick<ListViewBooking, 'effectiveStart' | 'effectiveEnd'>,
  now: string
): TemporalStatus {
  const nowObject = parseListViewDateTime(now);
  if (!nowObject) return 'unknown';
  const start = parseListViewDateTime(booking.effectiveStart);
  const end = parseListViewDateTime(booking.effectiveEnd);
  if (hasEnded(end, nowObject)) return 'past';
  if (start && end && !nowObject.isBefore(start) && !nowObject.isAfter(end))
    return 'active';
  if (start && start.isAfter(nowObject)) return 'future';
  return 'unknown';
}

export function formatCellValue(
  value: ListViewCellValue,
  /* Only column.type is ever read here, so callers that format a value
     outside of any real configured column (e.g. a child booking's time
     range) don't need to fabricate a full ListViewColumn */
  column: Pick<ListViewColumn, 'type'>,
  dateFormatSetting: string,
  timeFormatSetting: string
): string | null {
  if (value === null || value === undefined) return null;
  /* Multi-value custom data fields (e.g. multiple checkboxes) can hold an Array */
  if (Array.isArray(value))
    return value
      .map((item) => formatCellValue(item, column, dateFormatSetting, timeFormatSetting))
      .join(', ');
  const type = column.type;
  if (type === 'datetime' || type === 'date' || type === 'time') {
    const dateObject = parseListViewDateTime(value);
    if (dateObject === null) return String(value);
    /* The printers of dateTimePrinting, so a cell reads the way the same
       value reads everywhere else in the app: the configured setting is the
       key to a format, not a format itself (the 'h:mm a' setting displays as
       'h:mm A', for one) */
    const dateString = () => printInInputDateFormat(dateObject, dateFormatSetting);
    const timeString = () => printInHourDisplayFormat(dateObject, timeFormatSetting);
    if (type === 'date') return dateString();
    if (type === 'time') return timeString();
    return `${dateString()} ${timeString()}`;
  }
  return String(value);
}

/* The period a booking is on for, as one 'start-end' range of times: what a
 * card shows next to its title, and what a set's member shows at the right of
 * its row. Two columns of their own would say the same thing in more room.
 *
 * The effective start/end are read, so a set falls back to the outer bounds of
 * its members the way it does everywhere else. An open end reads as a dash. */
export function bookingTimeRange(
  booking: ListViewBooking,
  timeFormatSetting: string
): string {
  const timeColumn = { type: 'time' };
  const start = formatCellValue(
    booking.effectiveStart,
    timeColumn,
    '',
    timeFormatSetting
  );
  const end = formatCellValue(booking.effectiveEnd, timeColumn, '', timeFormatSetting);
  return `${start || '-'}–${end || '-'}`;
}

export function visibleColumns(configuration: ListViewConfiguration): ListViewColumn[] {
  const columns = configuration.columns || [];
  return columns.filter((column) => column.visible !== false);
}

/* The columns the views lay out, which is the visible ones without the count:
 * that one is shown beside the resource's name instead of in a column of its
 * own.
 *
 * It stays a configured column all the same, and visibleColumns keeps
 * reporting it: the snapshot a refresh is diffed against is taken per visible
 * column (see listViewDiff), so a count that changed is still a change the
 * views can mark - on the name it is shown beside. */
export function renderedColumns(configuration: ListViewConfiguration): ListViewColumn[] {
  return visibleColumns(configuration).filter(
    (column) => column.key !== COUNT_COLUMN_KEY
  );
}

/* How many of the resource a booking books: shown beside the name whatever it
   is, a booking of one included - the count is read as a value of the booking,
   and one that is only there above one reads as a value that went missing.
   Null only for a booking that reports no count at all. */
export function bookingCount(booking: ListViewBooking): number | null {
  return booking.number;
}

/* Group the bookings on the configured groupBy column (per day when that column
 * holds a datetime). Returns an Array of { key, dateObject, label, bookings },
 * sorted chronologically/alphabetically, with the group of bookings without a
 * value for the groupBy column at the end. Without a groupBy configuration a
 * single group holding all bookings is returned. */
export function groupBookingsByDay(
  bookings: ListViewBooking[],
  configuration: ListViewConfiguration
): ListViewGroup[] {
  const groupBy = configuration.groupBy;
  if (!groupBy || !groupBy.column)
    return [{ key: null, dateObject: null, label: null, bookings }];
  const column: ListViewColumn = { key: groupBy.column };
  const groups = new Map<string | null, ListViewGroup>();
  bookings.forEach((booking) => {
    const value = bookingCellValue(booking, column);
    const dateObject = parseListViewDateTime(value);
    /* The groupBy column is always configured to hold a string-ish value in
       practice (a date/datetime or plain text field); bookingCellValue's
       broader ListViewCellValue return type isn't narrowed to that here */
    const key = dateObject ? dateObject.format('YYYY-MM-DD') : (value as string | null);
    const existingGroup = groups.get(key);
    if (existingGroup) existingGroup.bookings.push(booking);
    else
      groups.set(key, {
        key,
        dateObject: dateObject ? dateObject.startOf('day') : null,
        label: key === null ? null : String(key),
        bookings: [booking]
      });
  });
  const sortValue = (group: ListViewGroup): number | string | null => {
    if (group.key === null) return null;
    return group.dateObject ? group.dateObject.valueOf() : group.label;
  };
  /* The bookings are already sorted chronologically by the server */
  return [...groups.values()].sort((groupA, groupB) => {
    const valueA = sortValue(groupA);
    const valueB = sortValue(groupB);
    if (valueA === valueB) return 0;
    /* Bookings without a group value sort last */
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    return valueA < valueB ? -1 : 1;
  });
}

/* A filter: { id, category: 'custom'|'system', fieldKeyword, fieldName, fieldType,
 * options, values }. A booking matches a filter when it matches one of the filter's
 * values (OR); it is kept when it matches all active filters (AND). Fields with
 * predefined options (fieldType 'dropdown', or any other type that has a
 * non-empty options list, e.g. Y/N) match exactly; fields without match a
 * case-insensitive substring. This mirrors ListViewFilters.fieldUsesOptions,
 * which decides on the same basis whether to show checkboxes or a text input. */
function matchesFilterValues(
  fieldValue: ListViewCellValue,
  filter: ActiveFilter
): boolean {
  if (fieldValue === null || fieldValue === undefined) return false;
  const values = Array.isArray(fieldValue)
    ? fieldValue.map(String)
    : [String(fieldValue)];
  if (filter.fieldType.toLowerCase() === 'dropdown' || filter.options.length > 0)
    return values.some((value) => filter.values.includes(value));
  return values.some((value) =>
    filter.values.some((filterValue) =>
      value.toLowerCase().includes(filterValue.toLowerCase())
    )
  );
}

/* The column a filter reads its field through: a custom data field as
 * 'customdata:<keyword>', a system field as the DTO field itself. Filtering
 * reads the booking through this column, and so does the snapshot a refresh
 * is diffed against (see listViewDiff): a booking deleted in Yesplan is
 * rebuilt from its snapshot values only, and it can only still match the
 * filters it matched before when the snapshot holds what they read. */
export function filterColumn(filter: ActiveFilter): ListViewColumn {
  return {
    key:
      filter.category === 'custom'
        ? `${CUSTOM_DATA_KEY_PREFIX}${filter.fieldKeyword}`
        : filter.fieldKeyword
  };
}

function matchesFilter(booking: ListViewBooking, filter: ActiveFilter): boolean {
  if (filter.values.length === 0) return true;
  const fieldValue = bookingCellValue(booking, filterColumn(filter));
  if (filter.category === 'system' && filter.fieldKeyword === 'hasStarted')
    return filter.values.some((value) =>
      value === 'Started' ? fieldValue === true : fieldValue !== true
    );
  return matchesFilterValues(fieldValue, filter);
}

export function filterBookings(
  bookings: ListViewBooking[],
  activeFilters: ActiveFilter[]
): ListViewBooking[] {
  if (activeFilters.length === 0) return bookings;
  return bookings.filter((booking) =>
    activeFilters.every((filter) => matchesFilter(booking, filter))
  );
}

/* The bookings that have not ended yet, for the toolbar's "hide past bookings"
   switch. Top-level bookings only, like the filters above: a set's effective
   end is the latest of its members', so it drops out only once everything in
   it is over. The clock is parsed once for the list rather than once per
   booking - this runs on every render, the minute tick included - and an
   unparseable one selects nothing away. */
export function withoutPastBookings(
  bookings: ListViewBooking[],
  now: string
): ListViewBooking[] {
  const nowObject = parseListViewDateTime(now);
  if (nowObject === null) return bookings;
  return bookings.filter(
    (booking) => !hasEnded(parseListViewDateTime(booking.effectiveEnd), nowObject)
  );
}

export function pageCount(totalCount: number, itemsPerPage: number): number {
  return Math.max(1, Math.ceil(totalCount / itemsPerPage));
}

export function paginateBookings(
  bookings: ListViewBooking[],
  page: number,
  itemsPerPage: number
): ListViewBooking[] {
  const startIndex = (page - 1) * itemsPerPage;
  return bookings.slice(startIndex, startIndex + itemsPerPage);
}

/* The rows of the table view: the top-level bookings, interleaved with the
 * members of every set that is open, each carrying the depth it sits at (0 for
 * a top-level booking). A set can hold another set, so the members are walked
 * recursively rather than one level deep: every booking under an open set gets
 * a row.
 *
 * A collapsed set contributes its own row only. Whether a set is open is asked
 * per booking rather than passed as a list of ids, so the walk stops at a
 * collapsed set instead of walking a tree it does not render. */
export function tableRows(
  bookings: ListViewBooking[],
  isSetExpanded: (bookingID: string) => boolean = () => true
): ListViewTableRow[] {
  const rowsOfBooking = (
    booking: ListViewBooking,
    depth: number,
    parentSet: ListViewBooking | null
  ): ListViewTableRow[] => [
    { ...booking, depth, parentSet },
    ...(isSetExpanded(booking.id)
      ? booking.children.flatMap((child) => rowsOfBooking(child, depth + 1, booking))
      : [])
  ];
  return bookings.flatMap((booking) => rowsOfBooking(booking, 0, null));
}

const sameCellValue = (
  value: ListViewCellValue,
  otherValue: ListViewCellValue
): boolean => {
  /* A multi-value custom data field holds an Array, which is a different
     object on each booking even when it says the same thing */
  if (Array.isArray(value) || Array.isArray(otherValue)) {
    const values = Array.isArray(value) ? value : [value];
    const otherValues = Array.isArray(otherValue) ? otherValue : [otherValue];
    return (
      values.length === otherValues.length &&
      values.every((item, index) => item === otherValues[index])
    );
  }
  return value === otherValue;
};

/* Whether this row would only repeat, in this column, what the set it belongs
 * to already says. Such a cell shows a ditto mark instead: a set's members
 * differ from their set in a couple of columns at most, and spelling the rest
 * out again on every member is what makes the block hard to read.
 *
 * The dates and times are exempt: they are what a booking row is there to
 * say, and a member booked for exactly its set's period is worth seeing as
 * such. So is the column the name is in, which is what tells the members
 * apart - the caller decides that one, being the one that knows which column
 * carries the nesting.
 *
 * A booking with no value of its own is not repeating anything, so it is
 * false there: that cell says "no value" rather than "the same as the set". */
export function repeatsParentSetValue(
  row: ListViewTableRow,
  column: ListViewColumn
): boolean {
  if (row.parentSet === null) return false;
  if (column.type === 'datetime' || column.type === 'date' || column.type === 'time')
    return false;
  const value = bookingCellValue(row, column);
  /* Nothing to repeat: the cell is a dash either way */
  if (value === null || value === '') return false;
  return sameCellValue(value, bookingCellValue(row.parentSet, column));
}

/* Converts a CustomDataTreeNode tree (see types.ts) into the shape the filter
 * builder drills through: { kind: 'group', name, children } or
 * { kind: 'field', category, name, keyword, fieldType, options } nodes. */
function customDataFilterItems(customDataTree: CustomDataTreeNode[]): FilterTreeItem[] {
  return customDataTree.map((node) => {
    if (node.type === 'group')
      return {
        kind: 'group',
        name: node.name,
        children: customDataFilterItems(node.children)
      };
    return {
      kind: 'field',
      category: 'custom',
      name: node.name,
      keyword: node.keyword,
      fieldType: node.fieldType,
      options: node.options
    };
  });
}

type FilterTreeGroup = Extract<FilterTreeItem, { kind: 'group' }>;

/* Nest a domain's system fields (always shown) and its custom data fields
   (omitted when there are none, e.g. the fetch is still pending) under one
   top-level filter group */
function domainFilterGroup(
  name: string,
  systemFields: FilterTreeField[],
  customDataTree: CustomDataTreeNode[]
): FilterTreeGroup {
  const customDataItems = customDataFilterItems(customDataTree);
  const systemGroup: FilterTreeGroup = {
    kind: 'group',
    name: i18n('System data'),
    children: systemFields
  };
  const children =
    customDataItems.length === 0
      ? [systemGroup]
      : [
          systemGroup,
          { kind: 'group' as const, name: i18n('Custom data'), children: customDataItems }
        ];
  return { kind: 'group', name, children };
}

/* Everything the filter tree is built out of. Named rather than positional:
   these are seven lists, most of them of plain strings, and at a call site
   they would otherwise be indistinguishable from one another. */
export interface FilterTreeSources {
  resourceCustomDataTree: CustomDataTreeNode[];
  resourceBookingCustomDataTree: CustomDataTreeNode[];
  resourceGroups: string[];
  resourceRoles: string[];
  bookedRoles: string[];
  eventStatuses: string[];
  eventProfiles: string[];
}

/* The tree is organized by domain first (a booking's own fields vs. those of
 * the resource it books vs. its event), each split into system (DTO) fields
 * and custom data fields, since resource and resourcebooking custom data are
 * distinct field schemas that just happen to be merged into one customData bag
 * on the booking DTO for filtering/display purposes. */
export function buildFilterTree({
  resourceCustomDataTree = [],
  resourceBookingCustomDataTree = [],
  resourceGroups = [],
  resourceRoles = [],
  bookedRoles = [],
  eventStatuses = [],
  eventProfiles = []
}: Partial<FilterTreeSources>): FilterTreeItem[] {
  const systemField = (
    name: string,
    keyword: string,
    fieldType: string,
    options: string[] = []
  ): FilterTreeField => ({
    kind: 'field',
    category: 'system',
    name,
    keyword,
    fieldType,
    options
  });
  const resourceGroup = domainFilterGroup(
    i18n('Resource'),
    [
      systemField(i18n('Resource name'), 'resourceName', 'text'),
      /* The values the Yesplan API uses for resourcetype (lowercase) */
      systemField(i18n('Resource type'), 'resourceType', 'dropdown', [
        'human',
        'material',
        'location',
        'service',
        'financial',
        'placeholder',
        'set'
      ]),
      systemField(i18n('Resource group'), 'resourceGroup', 'dropdown', resourceGroups),
      /* The roles the resource can fulfil, of which it can have several;
         resourceRoles is matched like any other multi-value field (see
         matchesFilterValues). The role a booking actually assigns is a field
         of the booking, below. */
      systemField(i18n('Roles'), 'resourceRoles', 'dropdown', resourceRoles),
      /* Free-text, like comment/eventName/locationName/eventTopGroup: a resource's
         description has no finite set of "all possible values" to fetch */
      systemField(i18n('Description'), 'resourceDescription', 'text')
    ],
    resourceCustomDataTree
  );
  const resourceBookingGroup = domainFilterGroup(
    i18n('Resourcebooking'),
    [
      systemField(i18n('Comment'), 'comment', 'text'),
      /* The role the booking assigns, which can be one the booked resource
         does not have itself - so its options cannot come from the resource
         role list alone (see bookedRolesOfBookings) */
      systemField(i18n('Role'), 'role', 'dropdown', bookedRoles),
      systemField(i18n('Started'), 'hasStarted', 'dropdown', ['Started', 'Not started'])
    ],
    resourceBookingCustomDataTree
  );
  const eventGroup: FilterTreeGroup = {
    kind: 'group',
    name: i18n('Event'),
    children: [
      systemField(i18n('Event'), 'eventName', 'text'),
      systemField(i18n('Location'), 'locationName', 'text'),
      systemField(i18n('Group'), 'eventDirectGroup', 'text'),
      systemField(i18n('Top group'), 'eventTopGroup', 'text'),
      /* Both are fixed value lists of the installation, fetched once via
         /eventoptions: several of them can be ticked, like any other dropdown.
         The options are names, which is what the booking DTO carries - the
         status and profile ids never reach the client here. The order is the
         one the route hands over: the statuses in the installation's own
         (workflow) order, the profiles alphabetically. */
      systemField(i18n('Status'), 'eventStatus', 'dropdown', eventStatuses),
      systemField(i18n('Profile'), 'eventProfile', 'dropdown', eventProfiles)
    ]
  };
  return [resourceGroup, resourceBookingGroup, eventGroup];
}

/* Visit every booking in the list and everything nested under it, at any
   depth: what the filter option collectors below all walk. */
function forEachBookingInTree(
  bookings: ListViewBooking[],
  visit: (booking: ListViewBooking) => void
): void {
  bookings.forEach((booking) => {
    visit(booking);
    forEachBookingInTree(booking.children, visit);
  });
}

/* All distinct resource groups appearing in the bookings or their children.
   The resource group filter prefers the complete list fetched via
   /resourceoptions (every group on the installation, not just the ones with a
   booking in the current range); this is the fallback used while that fetch
   is still pending or failed. */
export function resourceGroupsOfBookings(bookings: ListViewBooking[]): string[] {
  const groups = new Set<string>();
  forEachBookingInTree(bookings, (booking) => {
    if (booking.resourceGroup) groups.add(booking.resourceGroup);
  });
  return [...groups].sort();
}

/* All distinct resource roles appearing in the bookings or their children
   (a resource can have several). The role filter prefers the complete list
   fetched via /resourceoptions; this is the fallback used while that fetch is
   still pending or failed. */
export function resourceRolesOfBookings(bookings: ListViewBooking[]): string[] {
  const roles = new Set<string>();
  forEachBookingInTree(bookings, (booking) => {
    (booking.resourceRoles || []).forEach((role) => roles.add(role));
  });
  return [...roles].sort();
}

export function eventStatusesOfBookings(bookings: ListViewBooking[]): string[] {
  const statuses = new Set<string>();
  forEachBookingInTree(bookings, (booking) => {
    if (booking.eventStatus) statuses.add(booking.eventStatus);
  });
  return [...statuses].sort();
}

export function eventProfilesOfBookings(bookings: ListViewBooking[]): string[] {
  const profiles = new Set<string>();
  forEachBookingInTree(bookings, (booking) => {
    if (booking.eventProfile) profiles.add(booking.eventProfile);
  });
  return [...profiles].sort();
}

/* The options of the booked role filter: the roles the loaded bookings assign,
 * plus every role of the installation.
 *
 * Unlike the other dropdowns this cannot be the /resourceoptions list on its
 * own: those are the roles resources have, and a booking can assign a role no
 * resource has (Frank, an Engineer and Stage Hand, booked as 'External' for
 * one booking). The bookings in view are the only place such a role shows up,
 * so both sources are needed.
 */
export function bookedRolesOfBookings(
  bookings: ListViewBooking[],
  resourceRoles: string[]
): string[] {
  const roles = new Set<string>(resourceRoles);
  forEachBookingInTree(bookings, (booking) => {
    if (booking.role) roles.add(booking.role);
  });
  return [...roles].sort();
}

/* Find the tree node of an active filter's field. The tree is the fresher
   source: it carries the field's current option list, which the filter's own
   copy (stored per user) can predate. Null when the field is gone from the
   tree entirely - a field that was removed in Yesplan, say. */
export function findFilterTreeField(
  items: FilterTreeItem[],
  filter: ActiveFilter
): FilterTreeField | null {
  for (const item of items) {
    if (item.kind === 'group') {
      const found = findFilterTreeField(item.children, filter);
      if (found) return found;
    } else if (item.keyword === filter.fieldKeyword && item.category === filter.category)
      return item;
  }
  return null;
}

/* Whether a field is filtered by picking from a list of values rather than by
   typing a term: dropdowns always are, and so is any field that turns out to
   have options */
export function filterFieldUsesOptions(field: FilterTreeField): boolean {
  return field.fieldType.toLowerCase() === 'dropdown' || field.options.length > 0;
}
